import BigNumber from 'bignumber.js';
import { ethers } from 'ethers';
import React, { useEffect, useMemo, useState } from 'react';
import { useAccount } from 'wagmi';
import { CPGABI } from '../../ABI/ABI';
import {
    connectWalletSession,
    getMyPaymentDisputes,
    getStoredBackendToken,
    storeBackendToken,
} from '../../api/backendClient';
import ConnectWalletButton from '../../components/connectWallet/ConnectWalletButton';
import DisputeMessageThread from '../../components/dispute/DisputeMessageThread';
import SwitchNetworkButton from '../../components/connectWallet/SwitchNetworkButton';
import {
    CPGAddress,
    initialTokenList,
    targetChainId,
} from '../../constant/constant';
import { copyIcon } from '../../constant/icon';
import { resolveAttachmentUrl } from '../../utils/attachmentUrl';
import {
    copyToClipboard,
    getDueDate,
    getRaisedDisputeDate,
    shortenWalletAddress,
} from '../../utils/commonFunction';
import { readContractData } from '../../utils/contractInstance';
import {
    fetchDisputesForAccount,
    normalizeDispute,
    normalizeLockedPayment,
} from '../../utils/cpgDiamondReader';

// ---------------- helpers ----------------

const STATUS_OPEN = 'open';
const STATUS_RESOLVED = 'resolved';
const STATUS_UNDER_REVIEW = 'under_review';

const RESOLVED_DB_STATUSES = new Set(['resolved', 'closed']);
const REVIEW_DB_STATUSES = new Set([
    'under_review',
    'review',
    'reviewing',
    'in_review',
    'processing',
]);

const deriveDisplayStatus = ({ resolved, dbStatus }) => {
    if (resolved || RESOLVED_DB_STATUSES.has(dbStatus)) return STATUS_RESOLVED;
    if (REVIEW_DB_STATUSES.has(dbStatus)) return STATUS_UNDER_REVIEW;
    return STATUS_OPEN;
};

const toIsoDate = (input) => {
    if (!input || input === '—') return '—';
    if (typeof input === 'string') {
        const ddmmyyyy = input.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (ddmmyyyy) {
            const [, d, m, y] = ddmmyyyy;
            return `${y}-${m}-${d}`;
        }
        const parsed = new Date(input);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed.toISOString().slice(0, 10);
        }
        return input;
    }
    try {
        const d = new Date(input);
        if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    } catch (_e) {}
    return '—';
};

const getClaimedAmountWei = (
    claimedMilestones,
    totalPayment,
    totalMilestones,
) => {
    try {
        const claimed = new BigNumber(claimedMilestones.toString());
        const total = new BigNumber(totalPayment.toString());
        const ms = new BigNumber(totalMilestones.toString());
        if (ms.isZero()) return '0';
        const perMs = total.dividedBy(ms);
        return claimed.multipliedBy(perMs).toFixed();
    } catch (_e) {
        return '0';
    }
};

// ---------------- presentational pieces ----------------

const StatCard = ({ label, value, hint, icon, accent = 'slate', loading }) => {
    const accentMap = {
        slate: 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/60',
        amber: 'border-amber-200 bg-amber-50/90 dark:border-amber-900/40 dark:bg-amber-950/30',
        emerald:
            'border-teal-200 bg-teal-50/90 dark:border-teal-900/40 dark:bg-teal-950/30',
        rose: 'border-rose-200 bg-rose-50/90 dark:border-rose-900/40 dark:bg-rose-950/30',
    };
    const iconMap = {
        amber: 'text-amber-700 dark:text-amber-400',
        emerald: 'text-teal-700 dark:text-teal-400',
        slate: 'text-zinc-500 dark:text-zinc-500',
        rose: 'text-rose-700 dark:text-rose-400',
    };
    return (
        <div
            className={`rounded-2xl border px-5 py-5 ${accentMap[accent] || accentMap.slate}`}
        >
            <div className="flex items-start justify-between gap-3">
                <p className="text-xs text-zinc-600 dark:text-zinc-400">
                    {label}
                </p>
                <i
                    className={`${icon} text-sm ${iconMap[accent] || iconMap.slate}`}
                />
            </div>
            <div className="mt-2 text-3xl font-bold tabular-nums text-zinc-900 dark:text-white">
                {loading ? <span className="text-zinc-400">…</span> : value}
            </div>
            {hint && (
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-500">
                    {hint}
                </p>
            )}
        </div>
    );
};

const StatusPill = ({ status }) => {
    if (status === STATUS_RESOLVED) {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-800 dark:border-teal-900/50 dark:bg-teal-950/40 dark:text-teal-300">
                <i className="fa-solid fa-circle-check text-[10px]" />
                Resolved
            </span>
        );
    }
    if (status === STATUS_UNDER_REVIEW) {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-800 dark:border-sky-900/50 dark:bg-sky-950/40 dark:text-sky-300">
                <i className="fa-solid fa-eye text-[10px]" />
                Under Review
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300">
            <i className="fa-regular fa-clock text-[10px]" />
            Open
        </span>
    );
};

const DisputeRow = ({ row, onDiscussion }) => {
    const disputeIdLabel = `DSP-${String(row.disputeIdLabelSource).padStart(3, '0')}`;
    const paymentIdLabel =
        row.paymentId != null
            ? `PAY-${String(row.paymentId).padStart(4, '0')}`
            : '—';
    return (
        <div className="grid grid-cols-[repeat(14,minmax(0,1fr))] items-center gap-3 border-b border-zinc-200 px-5 py-4 transition-colors hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900/25">
            <div className="col-span-2 sm:col-span-1">
                <span className="font-mono text-xs font-semibold tracking-wide text-rose-700 dark:text-rose-400">
                    {disputeIdLabel}
                </span>
            </div>
            <div className="hidden sm:col-span-1 sm:block">
                <span className="font-mono text-xs font-semibold tracking-wide text-teal-700 dark:text-teal-400">
                    {paymentIdLabel}
                </span>
            </div>
            <div className="hidden min-w-0 items-center gap-2 lg:col-span-2 lg:flex">
                <span className="truncate font-mono text-sm text-zinc-800 dark:text-zinc-200">
                    {row.senderAddress
                        ? shortenWalletAddress(row.senderAddress)
                        : '—'}
                </span>
                {row.senderAddress && (
                    <button
                        type="button"
                        onClick={() => copyToClipboard(row.senderAddress)}
                        className="text-zinc-400 transition-colors hover:text-teal-700 dark:hover:text-teal-400"
                        aria-label="Copy sender address"
                    >
                        <i className={`${copyIcon} text-xs`} />
                    </button>
                )}
            </div>
            <div className="hidden min-w-0 items-center gap-2 lg:col-span-2 lg:flex">
                <span className="truncate font-mono text-sm text-zinc-800 dark:text-zinc-200">
                    {row.receiverAddress
                        ? shortenWalletAddress(row.receiverAddress)
                        : '—'}
                </span>
                {row.receiverAddress && (
                    <button
                        type="button"
                        onClick={() => copyToClipboard(row.receiverAddress)}
                        className="text-zinc-400 transition-colors hover:text-teal-700 dark:hover:text-teal-400"
                        aria-label="Copy receiver address"
                    >
                        <i className={`${copyIcon} text-xs`} />
                    </button>
                )}
            </div>
            <div className="col-span-2 sm:col-span-1">
                <span className="font-bold tabular-nums text-zinc-900 dark:text-white">
                    {row.amount}
                </span>
                {row.tokenName && row.tokenName !== '—' && (
                    <span className="ml-1 text-xs text-zinc-500 dark:text-zinc-500">
                        {row.tokenName}
                    </span>
                )}
            </div>
            <div className="col-span-3 truncate text-sm text-zinc-700 dark:text-zinc-300 sm:col-span-2">
                {row.reason ? (
                    <span title={row.reason}>{row.reason}</span>
                ) : (
                    <span className="text-zinc-400">—</span>
                )}
            </div>
            <div className="col-span-3 sm:col-span-2">
                <StatusPill status={row.displayStatus} />
            </div>
            <div className="col-span-3 sm:col-span-3 flex flex-wrap items-center justify-end gap-2">
                {row.canDiscuss ? (
                    <button
                        type="button"
                        onClick={() => onDiscussion(row)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-800 transition-colors hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                    >
                        <i className="fa-regular fa-comments" />
                        Discussion
                    </button>
                ) : null}
            </div>
        </div>
    );
};

const TableSkeleton = ({ rows = 4 }) => (
    <div>
        {Array.from({ length: rows }).map((_, i) => (
            <div
                key={i}
                className="grid grid-cols-[repeat(14,minmax(0,1fr))] gap-3 border-b border-zinc-200 px-5 py-4 dark:border-zinc-800"
            >
                <div className="col-span-1 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-1 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-2 hidden h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800 lg:block" />
                <div className="col-span-2 hidden h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800 lg:block" />
                <div className="col-span-1 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-2 h-3 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-2 h-5 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800" />
                <div className="col-span-3 h-7 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
            </div>
        ))}
    </div>
);

const EmptyState = () => (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-zinc-200 bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800/60">
            <i className="fa-solid fa-shield-halved text-zinc-500 dark:text-zinc-400" />
        </div>
        <h4 className="font-semibold text-zinc-800 dark:text-zinc-200">
            No disputes found
        </h4>
        <p className="mt-1 max-w-sm text-sm text-zinc-600 dark:text-zinc-500">
            When you report a problem on a payment, it will show up here so you
            can track its progress.
        </p>
        {/* <button
            type="button"
            onClick={onReport}
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-rose-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-rose-800 dark:bg-rose-600 dark:hover:bg-rose-500"
        >
            <i className="fa-solid fa-plus text-xs" />
            Report Problem
        </button> */}
    </div>
);

// ---------------- main page ----------------

const Dispute = () => {
    const [allDisputes, setAllDisputes] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [caseModalRow, setCaseModalRow] = useState(null);
    const [backendToken, setBackendToken] = useState('');

    const { isConnected, address: walletAddress, chainId } = useAccount();

    const handleDiscussionClick = (row) => setCaseModalRow(row);

    // ---------- data fetch ----------
    const getAllDisputes = async () => {
        try {
            setDataLoading(true);

            // Acquire / reuse backend session token (best-effort).
            let token = '';
            try {
                token = getStoredBackendToken(walletAddress) || '';
                if (!token && walletAddress) {
                    const session = await connectWalletSession({
                        walletAddress,
                        rolePreference: 'employee',
                    });
                    token = session.token;
                    storeBackendToken(walletAddress, token);
                }
                setBackendToken(token);
            } catch (sessionErr) {
                console.warn('Backend sign-in (optional):', sessionErr);
            }

            let backendDisputes = [];
            if (token) {
                try {
                    backendDisputes = await getMyPaymentDisputes(token);
                } catch (e) {
                    console.warn('Could not load backend disputes', e);
                }
            }

            const byDisputeId = new Map();
            const byPaymentId = new Map();
            for (const b of backendDisputes) {
                if (b.disputeId != null && String(b.disputeId).trim() !== '') {
                    byDisputeId.set(String(b.disputeId), b);
                }
                if (b.paymentId != null && String(b.paymentId).trim() !== '') {
                    byPaymentId.set(String(b.paymentId), b);
                }
            }

            const matchedBackendIds = new Set();
            const rows = [];

            // ----- on-chain disputes (Diamond: disputesByAccount mapping) -----
            const canReadChain =
                Boolean(walletAddress) &&
                Number(chainId) === Number(targetChainId);

            if (canReadChain) {
                const chainDisputes = await fetchDisputesForAccount(
                    readContractData,
                    CPGAddress,
                    CPGABI,
                    walletAddress,
                );
                for (const disputeEntry of chainDisputes) {
                    const disputeData = normalizeDispute(disputeEntry);
                    const disputeId = Number(disputeData.disputeId);
                    const paymentIdNum = Number(disputeData.paymentId);
                    const rawPayment = await readContractData(
                        CPGAddress,
                        CPGABI,
                        'lockedPayments',
                        [paymentIdNum],
                    );
                    const paymentData = normalizeLockedPayment(rawPayment);
                    const processedPaymentData = {
                        id: paymentData.paymentId,
                        sender: paymentData.sender,
                        receiver: paymentData.receiver,
                        tokenAddress: paymentData.tokenAddress,
                        amount: paymentData.totalAmount,
                        milestone: paymentData.milestones,
                        milestoneInterval: paymentData.milestoneInterval,
                        claimedMilestone: paymentData.claimedMilestones,
                        startTime: paymentData.startTime,
                    };

                    const backendMatch =
                        byDisputeId.get(String(disputeId)) ||
                        byPaymentId.get(String(paymentIdNum));
                    if (backendMatch?.id)
                        matchedBackendIds.add(backendMatch.id);

                    const tokenInfo = initialTokenList.find(
                        (t) =>
                            t.address === processedPaymentData.tokenAddress,
                    );
                    if (!tokenInfo) continue;

                    const { name, decimals, logoURI } = tokenInfo;
                    const formatted = parseFloat(
                        ethers.formatUnits(
                            processedPaymentData.amount,
                            decimals,
                        ),
                    );
                    const amount = Number.isInteger(formatted)
                        ? formatted
                        : formatted.toFixed(2);

                    const dueDate = getDueDate(disputeData.resolvedAt) || 0;
                    const raisedDate = getRaisedDisputeDate(disputeData.raisedAt);
                    const resolveDate = getRaisedDisputeDate(
                        disputeData.resolvedAt,
                    );
                    const claimedAmountWei = getClaimedAmountWei(
                        processedPaymentData.claimedMilestone,
                        processedPaymentData.amount,
                        processedPaymentData.milestone,
                    );

                    const resolved = Boolean(disputeData.resolved);
                    const displayStatus = deriveDisplayStatus({
                        resolved,
                        dbStatus: backendMatch?.status,
                    });

                    rows.push({
                        id: `chain-${disputeId}`,
                        disputeIdLabelSource: disputeId,
                        paymentId: paymentIdNum,
                        raisedBy: disputeData.raisedBy,
                        resolved,
                        displayStatus,
                        raisedTimestamp: disputeData.raisedAt,
                        resolvedTimestamp: disputeData.resolvedAt,
                        amount,
                        tokenName: name,
                        tokenImage: logoURI,
                        tokenDecimal: decimals,
                        claimedAmount: ethers.formatUnits(
                            claimedAmountWei,
                            decimals,
                        ),
                        senderAddress: processedPaymentData.sender,
                        receiverAddress: processedPaymentData.receiver,
                        claimedMilestone:
                            processedPaymentData.claimedMilestone,
                        milestone: processedPaymentData.milestone,
                        milestoneInterval:
                            processedPaymentData.milestoneInterval,
                        raisedDate,
                        raisedIso: toIsoDate(raisedDate),
                        resolveDate,
                        dueDate,
                        reason: backendMatch?.reason || '',
                        backendDispute: backendMatch || null,
                        canDiscuss: !!backendMatch?.id,
                    });
                }
            }

            // ----- backend-only rows (no chain match) -----
            const enrichBackendRow = async (b) => {
                let resolved = RESOLVED_DB_STATUSES.has(b.status);
                let amount = '—';
                let tokenName = '—';
                let claimedAmount = '—';
                let dueDate = '—';
                let resolveDate = '—';
                const raisedDate = b.createdAt
                    ? new Date(b.createdAt).toLocaleString()
                    : '—';

                if (canReadChain) {
                    try {
                        let payId =
                            b.paymentId != null &&
                            String(b.paymentId).trim() !== ''
                                ? String(b.paymentId).trim()
                                : null;
                        if (b.disputeId) {
                            const disputeTuple = normalizeDispute(
                                await readContractData(
                                    CPGAddress,
                                    CPGABI,
                                    'disputes',
                                    [b.disputeId],
                                ),
                            );
                            payId = String(disputeTuple.paymentId);
                            resolved = Boolean(disputeTuple.resolved);
                            const rs = getRaisedDisputeDate(
                                disputeTuple.resolvedAt,
                            );
                            if (rs) resolveDate = rs;
                            dueDate = String(
                                getDueDate(disputeTuple.resolvedAt) || '—',
                            );
                        }
                        if (payId) {
                            const locked = normalizeLockedPayment(
                                await readContractData(
                                    CPGAddress,
                                    CPGABI,
                                    'lockedPayments',
                                    [payId],
                                ),
                            );
                            const tokenAddress = locked.tokenAddress;
                            const rawAmount = locked.totalAmount;
                            const milestone = locked.milestones;
                            const claimedMilestone = locked.claimedMilestones;
                            const tinf = initialTokenList.find(
                                (t) => t.address === tokenAddress,
                            );
                            if (tinf) {
                                const formatted = parseFloat(
                                    ethers.formatUnits(
                                        rawAmount,
                                        tinf.decimals,
                                    ),
                                );
                                amount = Number.isInteger(formatted)
                                    ? formatted
                                    : formatted.toFixed(2);
                                tokenName = tinf.name;
                                const ca = getClaimedAmountWei(
                                    claimedMilestone,
                                    rawAmount,
                                    milestone,
                                );
                                claimedAmount = ethers.formatUnits(
                                    ca,
                                    tinf.decimals,
                                );
                            }
                        }
                    } catch (_e) {
                        /* chain optional */
                    }
                }

                const displayStatus = deriveDisplayStatus({
                    resolved,
                    dbStatus: b.status,
                });

                return {
                    id: `db-${b.id}`,
                    disputeIdLabelSource: b.disputeId || b.id,
                    paymentId: b.paymentId ? Number(b.paymentId) : null,
                    raisedBy: b.raisedByWallet || '',
                    resolved,
                    displayStatus,
                    raisedTimestamp: null,
                    resolvedTimestamp: null,
                    amount,
                    tokenName,
                    tokenImage: '',
                    tokenDecimal: 18,
                    claimedAmount,
                    senderAddress: b.senderWallet || '',
                    receiverAddress: b.receiverWallet || '',
                    claimedMilestone: 0,
                    milestone: 0,
                    milestoneInterval: 0,
                    raisedDate,
                    raisedIso: toIsoDate(b.createdAt || raisedDate),
                    resolveDate: resolveDate || '—',
                    dueDate: dueDate || '—',
                    reason: b.reason || '',
                    backendDispute: b,
                    canDiscuss: true,
                };
            };

            for (const b of backendDisputes) {
                if (matchedBackendIds.has(b.id)) continue;
                rows.push(await enrichBackendRow(b));
            }

            setAllDisputes(rows);
        } catch (error) {
            console.log(error);
        } finally {
            setDataLoading(false);
        }
    };

    useEffect(() => {
        getAllDisputes();
        // eslint-disable-next-line
    }, [walletAddress, chainId]);

    // ---------- derived ----------
    const counts = useMemo(() => {
        let open = 0;
        let resolved = 0;
        let underReview = 0;
        for (const d of allDisputes) {
            if (d.displayStatus === STATUS_RESOLVED) resolved += 1;
            else if (d.displayStatus === STATUS_UNDER_REVIEW) underReview += 1;
            else open += 1;
        }
        return { open, resolved, underReview, total: allDisputes.length };
    }, [allDisputes]);

    const filteredRows = useMemo(() => {
        const q = searchInput.toLowerCase().trim();
        if (!q) return allDisputes;
        return allDisputes.filter((row) => {
            const senderMatch = (row.senderAddress || '')
                .toLowerCase()
                .includes(q);
            const receiverMatch = (row.receiverAddress || '')
                .toLowerCase()
                .includes(q);
            const reasonMatch = (row.reason || '').toLowerCase().includes(q);
            const tokenMatch = (row.tokenName || '').toLowerCase().includes(q);
            const idMatch = String(row.disputeIdLabelSource || '')
                .toLowerCase()
                .includes(q);
            const paymentMatch = String(row.paymentId ?? '').includes(q);
            return (
                senderMatch ||
                receiverMatch ||
                reasonMatch ||
                tokenMatch ||
                idMatch ||
                paymentMatch
            );
        });
    }, [allDisputes, searchInput]);

    return (
        <div className="crypto-page relative min-h-[calc(100vh-80px)]">
            <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                {!isConnected ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in">
                        <ConnectWalletButton />
                    </div>
                ) : chainId !== targetChainId ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in">
                        <SwitchNetworkButton />
                    </div>
                ) : (
                    <div className="animate-fade-in space-y-6">
                        {/* HEADER */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 dark:border-rose-900/50 dark:bg-rose-950/40">
                                    <i className="fa-solid fa-triangle-exclamation text-rose-700 dark:text-rose-400" />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 md:text-3xl dark:text-white">
                                        Disputes
                                    </h1>
                                    <p className="mt-0.5 text-sm text-zinc-600 dark:text-zinc-400">
                                        Manage and track payment disputes
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* STATS */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            <StatCard
                                label="Open Cases"
                                value={counts.open + counts.underReview}
                                hint="Requires attention"
                                accent="amber"
                                icon="fa-regular fa-clock"
                                loading={dataLoading}
                            />
                            <StatCard
                                label="Resolved Cases"
                                value={counts.resolved}
                                hint="Successfully closed"
                                accent="emerald"
                                icon="fa-solid fa-circle-check"
                                loading={dataLoading}
                            />
                            <StatCard
                                label="Total Cases"
                                value={counts.total}
                                hint="All time"
                                accent="slate"
                                icon="fa-solid fa-triangle-exclamation"
                                loading={dataLoading}
                            />
                        </div>

                        {/* TABLE CARD */}
                        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50 dark:shadow-none md:rounded-2xl">
                            <div className="border-b border-zinc-200 bg-zinc-50/80 px-5 py-4 dark:border-zinc-800 dark:bg-zinc-900/30">
                                <div className="relative">
                                    <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400 dark:text-zinc-500" />
                                    <input
                                        type="search"
                                        value={searchInput}
                                        onChange={(e) =>
                                            setSearchInput(e.target.value)
                                        }
                                        placeholder="Search disputes..."
                                        className="cpg-input w-full pl-9"
                                    />
                                </div>
                            </div>

                            <div className="hidden border-b border-zinc-200 bg-zinc-50/90 px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-500 sm:grid sm:grid-cols-[repeat(14,minmax(0,1fr))] sm:gap-3">
                                <div className="col-span-1">Dispute ID</div>
                                <div className="col-span-1">Payment ID</div>
                                <div className="hidden lg:block lg:col-span-2">
                                    Sender
                                </div>
                                <div className="hidden lg:block lg:col-span-2">
                                    Receiver
                                </div>
                                <div className="col-span-1">Amount</div>
                                <div className="col-span-2">Reason</div>
                                <div className="col-span-2">Status</div>
                                <div className="col-span-3 text-right">
                                    Actions
                                </div>
                            </div>

                            {/* Body */}
                            {dataLoading && filteredRows.length === 0 ? (
                                <TableSkeleton rows={4} />
                            ) : filteredRows.length === 0 ? (
                                <EmptyState />
                            ) : (
                                <div>
                                    {filteredRows.map((row) => (
                                        <DisputeRow
                                            key={row.id}
                                            row={row}
                                            onDiscussion={handleDiscussionClick}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Discussion modal */}
            {caseModalRow?.backendDispute?.id && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm dark:bg-black/60">
                    <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-zinc-200 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-950">
                        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
                            <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">
                                Dispute discussion
                            </h3>
                            <button
                                type="button"
                                onClick={() => setCaseModalRow(null)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-100 text-zinc-500 transition-colors hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                            >
                                <i className="fa-solid fa-xmark" />
                            </button>
                        </div>
                        <div className="space-y-4 p-5">
                            <p className="text-xs text-zinc-500 dark:text-zinc-500">
                                Payment ID{' '}
                                <span className="font-mono text-zinc-800 dark:text-zinc-200">
                                    {caseModalRow.backendDispute.paymentId ??
                                        '—'}
                                </span>
                                {caseModalRow.backendDispute.disputeId !=
                                    null &&
                                caseModalRow.backendDispute.disputeId !== '' ? (
                                    <>
                                        {' '}
                                        · On-chain dispute{' '}
                                        <span className="font-mono text-zinc-800 dark:text-zinc-200">
                                            #
                                            {
                                                caseModalRow.backendDispute
                                                    .disputeId
                                            }
                                        </span>
                                    </>
                                ) : null}
                            </p>
                            {caseModalRow.backendDispute.reason ? (
                                <div>
                                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-500">
                                        Reason
                                    </p>
                                    <p className="text-sm text-zinc-800 dark:text-zinc-200">
                                        {caseModalRow.backendDispute.reason}
                                    </p>
                                </div>
                            ) : null}
                            {caseModalRow.backendDispute.description ? (
                                <div>
                                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-500">
                                        Description
                                    </p>
                                    <p className="whitespace-pre-wrap text-sm text-zinc-700 dark:text-zinc-300">
                                        {
                                            caseModalRow.backendDispute
                                                .description
                                        }
                                    </p>
                                </div>
                            ) : null}
                            {caseModalRow.backendDispute.filePath ? (
                                <a
                                    href={resolveAttachmentUrl(
                                        caseModalRow.backendDispute.filePath,
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-300"
                                >
                                    <i className="fa-solid fa-paperclip" />
                                    View attachment
                                </a>
                            ) : null}
                            <DisputeMessageThread
                                disputeId={caseModalRow.backendDispute.id}
                                authToken={backendToken}
                                discussionMode={
                                    caseModalRow.backendDispute?.taskId
                                        ? 'task'
                                        : 'payment'
                                }
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dispute;
